import { Badge, Button, Card, Table } from 'react-bootstrap';

const inr = (v) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const days = (v) => (v == null ? '-' : `${v} business day${v === 1 ? '' : 's'}`);
const limit = (v) => (v == null ? 'Unlimited' : Number(v).toLocaleString('en-IN'));

/**
 * Org Admin > License: every plan side by side (prices, limits, support, features), so a request to buy or renew is an
 * informed one. `plans` are the requestable plans, `featureCatalog` the optional features, `current` the plan in use.
 * Choose fills in the request form below; it is off while a request is pending.
 */
function PlanComparison({ plans, featureCatalog, current, canChoose, onChoose }) {
  if (!plans || plans.length === 0) return null;
  const sorted = [...plans].sort((x, y) => Number(x.priceYearly) - Number(y.priceYearly));
  const rows = [
    ['Warehouses', (p) => limit(p.maxWarehouses)],
    ['Production units', (p) => limit(p.maxProductionUnits)],
    ['Retail stores', (p) => limit(p.maxRetailStores)],
    ['Users', (p) => limit(p.maxUsers)],
    ['Documents a month', (p) => limit(p.maxDocumentsPerMonth)],
    ['First reply from your account manager', (p) => days(p.accountManagerResponseDays)],
    ['If they have not replied, iBigDo replies within', (p) => days(p.supportResponseDays)],
    ['One-time onboarding', (p) => (p.onboardingFee == null || Number(p.onboardingFee) <= 0 ? 'None'
      : `${inr(p.onboardingFee)}${p.onboardingRequired ? ' (required)' : ' (optional)'}`)],
  ];

  return (
    <Card className="mt-3">
      <Card.Header><h6 className="mb-0">Compare plans</h6></Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Prices are per organization, excluding GST. Every plan includes purchasing, stock, sales with GST invoices, accounts,
          GST return workings, Tally export and import, and the standard roles. The rows below are what differs.
        </p>
        <Table size="sm" bordered responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th />
              {sorted.map((p) => (
                <th key={p.id} className="text-center">
                  <div>{p.name}</div>
                  {current?.id === p.id && <Badge bg="success">Your plan</Badge>}
                  {p.description && <div className="fw-normal text-muted small">{p.description}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Price</td>
              {sorted.map((p) => (
                <td key={p.id} className="text-center">
                  <div><strong>{inr(p.priceYearly)}</strong> / year</div>
                  <div className="text-muted small">
                    {Number(p.priceMonthly) > 0 ? `or ${inr(p.priceMonthly)} / month` : 'Yearly only'}
                  </div>
                </td>
              ))}
            </tr>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                {sorted.map((p) => <td key={p.id} className="text-center">{value(p)}</td>)}
              </tr>
            ))}
            {featureCatalog.map((f) => (
              <tr key={f.key}>
                <td>{f.label}</td>
                {sorted.map((p) => (
                  <td key={p.id} className="text-center">
                    {(p.features || []).includes(f.key)
                      ? <span className="text-success fw-bold" aria-label="Included">&#10003;</span>
                      : <span className="text-muted" aria-label="Not included">&ndash;</span>}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td />
              {sorted.map((p) => (
                <td key={p.id} className="text-center">
                  <Button size="sm" variant="outline-primary" disabled={!canChoose} onClick={() => onChoose(p)}>
                    {current?.id === p.id ? 'Renew this plan' : 'Choose this plan'}
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

export default PlanComparison;
